// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { z } from 'zod';

// Configuration specified in files that are not part of this build.
// To use this external configuration, add a script file to the web site that is loaded
// before the application code, and writes the config to the global "window" variable.
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const externalConfig = window.utility_config;

// The configuration can also be specified in an environment variable,
// where the value of the variable is the config object serialized as a JSON string.
// The environment variable is evaluated at build time and takes precedence over
// the external config.
const envConfigString = import.meta.env.VITE_UTILITY_CONFIG;

export default class ConfigReader<
  Shape extends z.ZodRawShape,
  Config extends z.core.$ZodObjectConfig,
  T extends z.ZodObject<Shape, Config>,
> {
  private schema: T;

  constructor(schema: T) {
    this.schema = schema;
  }

  loadConfig(): z.infer<T> {
    if (envConfigString !== undefined) {
      console.info(`Config from VITE_UTILITY_CONFIG:`, envConfigString);

      const envConfig = JSON.parse(envConfigString) as unknown;
      const parsedConfig = this.schema.parse(envConfig);

      window.utility_config = parsedConfig;
      return parsedConfig;
    }

    if (externalConfig !== undefined) {
      console.info(`Config from window.utility_config:`, externalConfig);
      return this.schema.parse(externalConfig);
    }

    throw new Error(
      `No configuration found. Make sure 'window.utility_config' is set before the UI code is loaded.`,
    );
  }
}
